import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeInternalPath, POST_AUTH_REDIRECT_KEY } from '@/lib/urls';
import { Loader2, CheckCircle, Smartphone, AlertCircle } from 'lucide-react';

const AuthCallbackPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'confirming' | 'redirecting' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processando confirmação...');

  useEffect(() => {
    const handleCallback = async () => {
      const hash = window.location.hash;
      const searchParams = new URLSearchParams(window.location.search);
      
      // Check if this is a confirmation link
      const isConfirmation = 
        hash.includes('access_token') || 
        searchParams.get('type') === 'signup' ||
        searchParams.get('type') === 'email' ||
        hash.includes('type=signup') ||
        hash.includes('type=email');

      if (!isConfirmation) {
        navigate('/auth', { replace: true });
        return;
      }

      // Extract tokens from hash
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');

      if (!accessToken || !refreshToken) {
        setStatus('error');
        setMessage('Link de confirmação inválido ou expirado.');
        setTimeout(() => navigate('/auth', { replace: true }), 3000);
        return;
      }

      // Step 1: Confirm email by setting session on the web
      setStatus('confirming');
      setMessage('Confirmando seu e-mail...');

      try {
        const { error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (error) {
          console.error('Error confirming email:', error);
          setStatus('error');
          setMessage('Erro ao confirmar e-mail. Tente novamente.');
          setTimeout(() => navigate('/auth', { replace: true }), 3000);
          return;
        }

        // Email confirmed successfully!
        setStatus('success');
        setMessage('E-mail confirmado com sucesso!');

        // Step 2: Detect if user came from mobile device
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

        if (isMobile) {
          // DON'T sign out here - it invalidates the tokens!
          // The app will use these same tokens to create its session
          
          // Wait a moment to show success message
          setTimeout(() => {
            setStatus('redirecting');
            setMessage('Abrindo o Orçamigo...');
            
            // Use simple deep link to trigger app opening with login callback
            window.location.href = `orcamigo://login-callback#access_token=${accessToken}&refresh_token=${refreshToken}`;
            
            // Fallback message if app doesn't open
            setTimeout(() => {
              setMessage('Se o app não abriu, abra-o manualmente. Seu login será automático!');
            }, 2500);
          }, 1500);
        } else {
          // Desktop: Keep session and redirect. Honor a pending post-auth
          // redirect (e.g. an invite deep link) captured before the OAuth
          // round-trip, else fall back to the dashboard.
          localStorage.setItem('orcamigo_first_login', 'true');
          let dest = '/dashboard';
          try {
            const pending = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
            if (pending) {
              dest = sanitizeInternalPath(pending, '/dashboard');
              sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
            }
          } catch {
            // ignore unavailable storage
          }
          setTimeout(() => {
            navigate(dest, { replace: true });
          }, 1500);
        }
      } catch (error) {
        console.error('Error in confirmation:', error);
        setStatus('error');
        setMessage('Erro ao processar confirmação.');
        setTimeout(() => navigate('/auth', { replace: true }), 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="text-center space-y-4 max-w-sm">
        {status === 'loading' && (
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
        )}
        {status === 'confirming' && (
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
        )}
        {status === 'redirecting' && (
          <Smartphone className="h-12 w-12 text-primary mx-auto animate-pulse" />
        )}
        {status === 'success' && (
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
        )}
        {status === 'error' && (
          <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
        )}
        
        <p className="text-lg font-medium text-foreground">{message}</p>
        
        {status === 'redirecting' && (
          <p className="text-sm text-muted-foreground">
            Se o app não abrir automaticamente, abra o Orçamigo manualmente.
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthCallbackPage;
